import * as React from "react";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { configureClient } from "@/api/client";
import { login as apiLogin, describeToken, refreshToken, sessionOrigin, type Credentials, type Session } from "@/api/auth";
import { ApiError, UnauthorizedError } from "@/api/errors";
import { PortalReauthRequired, forgetGrant, renewPortalSession, startPortalSignIn } from "./portal";

/**
 * The signed-in session. The token lives in memory and sessionStorage
 * (never a cookie: crossbar's CORS is `*`). "Acting account" is NOT here,
 * it is a URL segment, see account/use-account.ts.
 */
interface SessionState {
  session?: Session;
  status: "booting" | "anonymous" | "authenticated";
  /** Set while a login is waiting on a second factor. */
  pendingMfa?: { credentials: Credentials; keyType?: string; provider?: string };
  login: (c: Credentials) => Promise<"ok" | "mfa">;
  completeMfa: (code: string) => Promise<void>;
  /** Adopt a session minted elsewhere: the portal callback route. */
  adopt: (session: Session) => void;
  logout: () => void;
  boot: () => Promise<void>;
}

export const useSession = create<SessionState>()(
  persist(
    (set, get) => ({
      session: undefined,
      status: "booting",
      pendingMfa: undefined,

      async login(credentials) {
        try {
          const session = await apiLogin(credentials);
          set({ session, status: "authenticated", pendingMfa: undefined });
          return "ok";
        } catch (err) {
          if (err instanceof UnauthorizedError && err.kind === "mfa_required") {
            set({ pendingMfa: { credentials, keyType: err.multiFactorRequest?.key_type, provider: err.multiFactorRequest?.provider_name } });
            return "mfa";
          }
          throw err;
        }
      },

      async completeMfa(code) {
        const pending = get().pendingMfa;
        if (!pending) throw new Error("no login waiting on a second factor");
        const session = await apiLogin(pending.credentials, code);
        set({ session, status: "authenticated", pendingMfa: undefined });
      },

      adopt(session) {
        set({ session, status: "authenticated", pendingMfa: undefined });
      },

      logout() {
        forgetGrant();
        set({ session: undefined, status: "anonymous", pendingMfa: undefined });
      },

      async boot() {
        const stored = get().session;
        if (!stored?.token) {
          set({ status: "anonymous" });
          return;
        }
        if (stored.expiresAt && stored.expiresAt * 1000 < Date.now()) {
          set({ session: undefined, status: "anonymous" });
          return;
        }
        try {
          const fresh = await describeToken(stored.token);
          // token_auth returns claims, not the login payload, so keep the
          // login-time facts the claims do not carry. Where the token came
          // from, and the origins that go with it, are among them.
          set({
            session: {
              ...stored,
              ...fresh,
              origin: sessionOrigin(stored),
              apiOrigin: stored.apiOrigin,
              socketOrigin: stored.socketOrigin,
              accountName: fresh.accountName ?? stored.accountName,
              resellerId: fresh.resellerId ?? stored.resellerId,
              isReseller: fresh.isReseller || stored.isReseller,
              isMaster: fresh.isMaster || stored.isMaster,
            },
            status: "authenticated",
          });
        } catch (err) {
          // Only a definite rejection means the token is dead. A network
          // or proxy blip (dev server restart, deploy) keeps the stored
          // session; the next real request's 401 will sign out if needed.
          if (err instanceof ApiError && [401, 403, 404].includes(err.status)) {
            set({ session: undefined, status: "anonymous" });
          } else {
            set({ status: "authenticated" });
          }
        }
      },
    }),
    {
      name: "switchboard-session",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({ session: s.session }),
    },
  ),
);

configureClient({
  token: () => useSession.getState().session?.token,
  apiOrigin: () => useSession.getState().session?.apiOrigin,
  onUnauthorized: () => {
    if (useSession.getState().status === "authenticated") useSession.getState().logout();
  },
});

/** Seconds until the token lapses, or undefined when the server set no expiry. */
export function useTokenTtl(): number | undefined {
  const exp = useSession((s) => s.session?.expiresAt);
  const [, tick] = React.useReducer((n: number) => n + 1, 0);
  React.useEffect(() => {
    const t = window.setInterval(tick, 30_000);
    return () => window.clearInterval(t);
  }, []);
  if (!exp) return undefined;
  return Math.max(0, Math.floor(exp - Date.now() / 1000));
}

const REFRESH_AHEAD_S = 5 * 60;
let refreshTimer: number | undefined;
let renewUnsupported = false;

/**
 * Silent refresh, five minutes before `exp`.
 *
 * A Kazoo session renews with `PUT /v2/auth`, which crossbar only allows
 * when `auth_modules.cb_user_auth.renew` is true on the account; a 405
 * means it is off, in which case we stop trying and let the top bar warn.
 * A portal session has no such renewal: its token was minted, so it is
 * minted again, off the portal grant we still hold or, failing that, off
 * a fresh trip through the portal's authorize page.
 */
export function renewSession(session: Session): Promise<Session> {
  return sessionOrigin(session) === "portal" ? renewPortalSession() : refreshToken();
}

function scheduleRefresh() {
  window.clearTimeout(refreshTimer);
  const { session, status } = useSession.getState();
  if (status !== "authenticated" || !session?.expiresAt) return;
  const portal = sessionOrigin(session) === "portal";
  if (!portal && renewUnsupported) return;
  const delayMs = Math.max(0, (session.expiresAt - REFRESH_AHEAD_S) * 1000 - Date.now());
  refreshTimer = window.setTimeout(async () => {
    try {
      const fresh = await renewSession(session);
      useSession.setState((s) => ({ session: { ...s.session, ...fresh, token: fresh.token } }));
    } catch (err) {
      if (err instanceof PortalReauthRequired) {
        void startPortalSignIn(window.location.pathname + window.location.search);
        return;
      }
      if (err instanceof ApiError && (err.status === 405 || err.status === 412)) renewUnsupported = true;
      // Any other failure: the next request's 401 handles logout.
    }
  }, delayMs);
}

useSession.subscribe((s, prev) => {
  if (s.session?.token !== prev.session?.token || s.status !== prev.status) scheduleRefresh();
});

/** True when the token expires within the warning window and no refresh is possible. */
export function useExpiryWarning(): boolean {
  const ttl = useTokenTtl();
  return ttl !== undefined && ttl < REFRESH_AHEAD_S && renewUnsupported;
}
